/**
 * The Lexical node registry for editing Payload blog-post `content`.
 *
 * `PAYLOAD_LEXICAL_NODES` is the `Klass[]` to hand to `createEditor({ nodes })`
 * / `<LexicalComposer initialConfig={{ nodes }}>`. It pairs the stock Lexical
 * nodes for every standard block (heading/quote from @lexical/rich-text,
 * list/listitem from @lexical/list, table/row/cell from @lexical/table) with
 * the two vendored Payload-custom nodes (link/autolink, upload). Paragraph,
 * text, linebreak, tab and root are built into Lexical core and must NOT be
 * registered.
 *
 * `assertRoundTrippable` is the fail-loud guard the editor opens with: before
 * mounting an article body it walks the tree and refuses any node type we don't
 * have a registered class for, rather than silently dropping it on save.
 */
import { ListItemNode, ListNode } from "@lexical/list"
import { HeadingNode, QuoteNode } from "@lexical/rich-text"
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table"
import type { Klass, LexicalNode } from "lexical"

import { AutoLinkNode, LinkNode } from "./payload-link-node"
import { UploadNode } from "./payload-upload-node"

export const PAYLOAD_LEXICAL_NODES: ReadonlyArray<Klass<LexicalNode>> = [
  HeadingNode,
  QuoteNode,
  ListNode,
  ListItemNode,
  TableNode,
  TableRowNode,
  TableCellNode,
  LinkNode,
  AutoLinkNode,
  UploadNode,
]

/**
 * Lexical core built-ins that have no registered class in `PAYLOAD_LEXICAL_NODES`
 * (they're built into Lexical itself) but still round-trip. `root`/`paragraph`/
 * `text`/`linebreak`/`tab` are documented core types.
 */
const CORE_LEXICAL_NODE_TYPES: ReadonlyArray<string> = [
  "root",
  "paragraph",
  "text",
  "linebreak",
  "tab",
]

/**
 * Every node `type` we can faithfully round-trip: the registered classes above
 * (derived from each class's `getType()`, so there's no hand-written list to
 * drift from `PAYLOAD_LEXICAL_NODES`) plus the Lexical core built-ins. Anything
 * outside this set means an unsupported block (e.g. a Payload `block` node) that
 * would be corrupted on save.
 */
export const SUPPORTED_LEXICAL_NODE_TYPES: ReadonlySet<string> = new Set([
  ...CORE_LEXICAL_NODE_TYPES,
  ...PAYLOAD_LEXICAL_NODES.map((node) => node.getType()),
])

type LexicalTreeNode = {
  type?: unknown
  children?: unknown
}

type LexicalContent = {
  root?: unknown
}

function collectTypes(node: unknown, into: Set<string>): void {
  if (node === null || typeof node !== "object") {
    return
  }
  if (Array.isArray(node)) {
    for (const child of node) {
      collectTypes(child, into)
    }
    return
  }
  const candidate = node as LexicalTreeNode
  if (typeof candidate.type === "string") {
    into.add(candidate.type)
  }
  if (candidate.children !== undefined) {
    collectTypes(candidate.children, into)
  }
}

/**
 * Walk a serialized Lexical `content` tree and report any node `type` we have
 * no registered class for. `{ ok: true }` means every node round-trips;
 * `{ ok: false, unsupported }` lists the offending types (deduped) so the
 * editor can refuse to open rather than silently corrupt on save.
 */
export function assertRoundTrippable(
  content: unknown
): { ok: true } | { ok: false; unsupported: string[] } {
  const types = new Set<string>()
  const root = (content as LexicalContent)?.root
  collectTypes(root ?? content, types)

  const unsupported = Array.from(types).filter(
    (type) => !SUPPORTED_LEXICAL_NODE_TYPES.has(type)
  )
  if (unsupported.length > 0) {
    return { ok: false, unsupported }
  }
  return { ok: true }
}
